import { cn } from "@/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useHermesTheme, type HermesTheme } from "./hermesTheme";
import {
  HermesAlertIcon,
  HermesCheckIcon,
  HermesCloseIcon,
  HermesInfoIcon,
} from "./HermesIcon";
import {
  dismissHermesToast,
  useHermesToasts,
  type HermesToastData,
  type HermesToastTone,
} from "./hermesToast.store";

const DURATION_MS = 3500;
const EASE = [0.22, 1, 0.36, 1] as const;

const TONE: Record<
  HermesToastTone,
  { chip: string; icon: typeof HermesCheckIcon; role: "status" | "alert" }
> = {
  success: {
    chip: "bg-hermes-positive/10 text-hermes-positive",
    icon: HermesCheckIcon,
    role: "status",
  },
  error: {
    chip: "bg-hermes-negative/10 text-hermes-negative",
    icon: HermesAlertIcon,
    role: "alert",
  },
  info: {
    chip: "bg-hermes-info/15 text-hermes-info",
    icon: HermesInfoIcon,
    role: "status",
  },
};

const HermesToastItem = ({ toast }: { toast: HermesToastData }) => {
  // Sob o mouse (ou com o foco no "x") o aviso espera: dá tempo de ler.
  const [isPaused, setIsPaused] = useState(false);
  const { chip, icon: Icon, role } = TONE[toast.tone];

  useEffect(() => {
    if (isPaused) return;
    const timer = setTimeout(() => dismissHermesToast(toast.id), DURATION_MS);
    return () => clearTimeout(timer);
  }, [isPaused, toast.id]);

  return (
    <motion.li
      layout="position"
      initial={{ opacity: 0, x: 16, scale: 0.98 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 16, transition: { duration: 0.18 } }}
      transition={{ duration: 0.3, ease: EASE }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
      role={role}
      className="bg-hermes-bg border-hermes-border pointer-events-auto relative flex w-full items-start gap-3 rounded-2xl border py-3 pr-2 pl-3 shadow-[0_12px_40px_-12px_rgb(0_0_0/.25)]"
    >
      <span
        className={cn(
          "grid size-7 shrink-0 place-items-center rounded-full",
          chip
        )}
      >
        <Icon key="on" pop className="size-4" />
      </span>
      <p className="text-hermes-fg min-w-0 flex-1 py-0.5 text-sm text-pretty break-words">
        {toast.message}
      </p>
      <button
        type="button"
        aria-label="Fechar o aviso"
        onClick={() => dismissHermesToast(toast.id)}
        className="group hermes-focus-ring text-hermes-muted-fg hover:bg-hermes-muted hover:text-hermes-fg grid size-7 shrink-0 cursor-pointer place-items-center rounded-[0.7rem] transition-colors duration-150"
      >
        <HermesCloseIcon />
      </button>
    </motion.li>
  );
};

/**
 * Os avisos do Hermes: canto inferior direito, empilhados, entram deslizando
 * da direita e somem sozinhos em 3,5s (pausam sob o mouse). Erro é `role="alert"`,
 * o resto `role="status"`. Vive dentro do `HermesScreen`, que liga os tokens.
 */
export const HermesToaster = ({
  theme: themeProp,
}: {
  /** Sem ele vale o tema da tela em volta (`HermesScreen`). */
  theme?: HermesTheme;
}) => {
  const inheritedTheme = useHermesTheme();
  const theme = themeProp ?? inheritedTheme;
  const toasts = useHermesToasts();

  // Portal no body: a tela do Hermes rola (`overflow`) e o aviso, que desliza
  // para fora da margem ao entrar e sair, criava rolagem ou era cortado ali
  // dentro. Fora dela o `.hermes` precisa ser religado para valerem os tokens.
  return createPortal(
    <div
      data-theme={theme}
      // `.hermes` pinta o fundo; aqui a camada tem de ser transparente.
      style={{ backgroundColor: "transparent" }}
      className="hermes pointer-events-none fixed right-4 bottom-4 z-[1000002] w-[min(24rem,calc(100vw-2rem))]"
    >
      <ul aria-label="Avisos" className="flex flex-col gap-2">
        <AnimatePresence initial={false}>
          {toasts.map(toast => (
            <HermesToastItem key={toast.id} toast={toast} />
          ))}
        </AnimatePresence>
      </ul>
    </div>,
    document.body
  );
};
